import { easeBoth } from '../../../src/utils/anim.js'

import { convertRadiusToZoom } from './utils.js'

export default function AnimationController (app, mapInitialized, config) {
  let appPadding = { bottom: 0, top: 0, right: 0, left: 0 } // listens for map/changePadding events to alter

  const DEFAULT_FLYTO_SPEED = 0.9 // default speed for map animations employing the flyTo method. Expressed in screens per second
  const DEFAULT_FITBOUNDS_SPEED = 1.2 // default speed for map animations employing the fitBounds method. Expressed in screens per second
  const DEFAULT_EASETO_DURATION = 1000 // default speed for map animations employing the easeTo method. Expressed in ms
  const DEFAULT_LINEAR_DURATION = 500 // default speed for map animations employing the panBy method. Expressed in ms

  let globalAnimSpeedMultiplier = config.globalAnimSpeedMultiplier || 1.0 // global speed factor for animations, 2 = twice as fast, 0.5 = half as fast
  if (window.Cypress && config.cypressAnimSpeedMultiplier)
    globalAnimSpeedMultiplier = config.cypressAnimSpeedMultiplier // Cypress tests can override the speed multiplier

  app.bus.on('map/animateToPointWithRadius', ({ lat, lng, floorId = null, radius, relSpeed = 1.0, animOptions = {}, ...rest }) => {
    mapInitialized().then(async map => {
      if (floorId) {
        app.bus.send('map/changeFloor', { id: floorId })
      }
      const validRadius = Math.max(radius, config.minZoomRadius)
      const venueCenter = await app.bus.get('venueData/getVenueCenter')
      const convertedZoom = convertRadiusToZoom(validRadius, venueCenter, map, app.config.renderDiv)

      if (!animOptions.padding && !rest.padding)
        rest.padding = appPadding

      animOptions.speed = DEFAULT_FLYTO_SPEED * relSpeed * globalAnimSpeedMultiplier

      map.flyTo({
        center: [lng, lat],
        zoom: convertedZoom,
        ...animOptions,
        ...rest // allow passing other props, such as padding
      })
    })
  })

  const setPadding = (padding, map) => {
    if (padding === 'mol')
      padding = app.env.isDesktop() ? { left: 347, right: 55, top: 72, bottom: 22 } : { top: 0, bottom: 0, left: 0, right: 0 }

    map.setPadding(padding)
    appPadding = padding
  }

  app.bus.on('map/changePadding', ({ padding }) =>
    mapInitialized().then(map => setPadding(padding, map)))

  app.bus.on('map/getPadding', () => appPadding)

  app.bus.on('map/animateToPoint', ({ lat, lng, floorId = null, zoom = null, relSpeed = 1.0, animOptions = {} }) =>
    mapInitialized().then(map => {
      if (floorId) {
        app.bus.send('map/changeFloor', { id: floorId })
      }
      const validZoom = zoom !== null ? zoom : map.getZoom()
      if (!animOptions.padding)
        animOptions.padding = appPadding

      animOptions.speed = DEFAULT_FLYTO_SPEED * relSpeed * globalAnimSpeedMultiplier

      map.flyTo({ center: [lng, lat], zoom: validZoom, ...animOptions })
    }))

  app.bus.on('map/animateToView', ({ relSpeed = 1.0, ...params }) =>
    mapInitialized().then(map => {
      if (!params.padding)
        params.padding = appPadding

      params.duration = DEFAULT_EASETO_DURATION * (1 / relSpeed) * (1 / globalAnimSpeedMultiplier)

      map.easeTo({ easing: easeBoth, ...params })
    }))

  app.bus.on('map/pan', ({ offset, relSpeed }) =>
    mapInitialized().then(map => {
      const options = { duration: DEFAULT_LINEAR_DURATION * (1 / relSpeed) * (1 / globalAnimSpeedMultiplier) }
      map.panBy(offset, options)
    }))

  app.bus.on('map/centerInBounds', async ({ bounds, bearing = null, floorId = null, pitch = null, relSpeed = 1.0, animOptions = {} }) =>
    mapInitialized().then((map) => {
      if (floorId) {
        app.bus.send('map/changeFloor', { id: floorId })
      }
      const validBearing = bearing !== null ? bearing : map.getBearing()
      const validPitch = pitch !== null ? pitch : map.getPitch()
      const fbOptions = { bearing: validBearing, pitch: validPitch, ...animOptions }

      fbOptions.speed = DEFAULT_FITBOUNDS_SPEED * relSpeed * globalAnimSpeedMultiplier

      map.fitBounds([[bounds.w, bounds.s], [bounds.e, bounds.n]], fbOptions)
    }))
}
